/**
 * Choice restoration and condition evaluation for story engine.
 */

import type { SceneData, ChoiceCondition } from '../scenes/types'

export interface EvaluateConditionContext {
  flags: Record<string, boolean>
  getSavedState: () => {
    choices?: Record<string, number>
    inventory?: string[]
    consumedInventory?: string[]
    minigames?: Record<string, { selectedItems?: string[] }>
  } | null
}

/**
 * Evaluate a choice condition (for restore and runtime).
 */
export function evaluateChoiceCondition(condition: ChoiceCondition, ctx: EvaluateConditionContext): boolean {
  switch (condition.type) {
    case 'choice': {
      const savedState = ctx.getSavedState()
      if (!savedState?.choices) return false
      const choiceMade = savedState.choices[condition.sceneId]
      return choiceMade === condition.choiceNumber
    }
    case 'flag': {
      const flagValue = ctx.flags[condition.flag] || false
      return condition.value !== undefined ? flagValue === condition.value : flagValue
    }
    case 'inventory': {
      const savedState = ctx.getSavedState()
      const consumed = savedState?.consumedInventory || []
      if (consumed.includes(condition.item)) {
        return false
      }
      const inventory = savedState?.inventory || []
      if (inventory.includes(condition.item)) {
        return true
      }
      // Fallback: check minigame selected items (for baleni-s-rychlonozkou)
      const minigames = savedState?.minigames || {}
      const minigame = minigames['minihra-baleni-s-rychlonozkou']
      if (minigame?.selectedItems?.includes(condition.item)) {
        return true
      }
      return false
    }
    case 'not':
      return !evaluateChoiceCondition(condition.condition, ctx)
    case 'and':
      return condition.conditions.every((c) => evaluateChoiceCondition(c, ctx))
    case 'or':
      return condition.conditions.some((c) => evaluateChoiceCondition(c, ctx))
    default:
      console.warn('Unknown condition type:', (condition as ChoiceCondition & { type: string }).type)
      return false
  }
}

/**
 * Resolve choice result to scene ID for restoration (uses saved choices + conditions).
 */
export function resolveChoiceResultForRestore(
  result: string | { sceneId: string; when?: ChoiceCondition } | Array<{ sceneId: string; when?: ChoiceCondition }>,
  savedChoices: Record<string, number>,
  evaluateCondition: (condition: ChoiceCondition) => boolean,
): string | null {
  if (typeof result === 'string') return result

  if (!Array.isArray(result)) {
    if (result.when) {
      return evaluateCondition(result.when) ? result.sceneId : null
    }
    return result.sceneId
  }

  // Array of conditional results - find first matching condition
  // Items without 'when' act as default fallback
  for (const item of result) {
    // If no condition specified, use as default fallback
    if (!item.when) {
      return item.sceneId
    }
    // Check condition
    if (evaluateCondition(item.when)) {
      return item.sceneId
    }
  }
  return null
}

/**
 * Check if a choice result scene should be loaded based on saved choices.
 */
export function shouldLoadChoiceScene(
  sceneId: string,
  scenes: SceneData[],
  savedChoices: Record<string, number>,
  resolveChoiceResult: (
    result: Parameters<typeof resolveChoiceResultForRestore>[0],
    savedChoices: Record<string, number>,
  ) => string | null,
): boolean {
  for (const scene of scenes) {
    if (!scene.choices) continue
    const choice1Result = resolveChoiceResult(scene.choices.choice1.result, savedChoices)
    if (choice1Result === sceneId && savedChoices[scene.id] === 1) return true
    const choice2Result = resolveChoiceResult(scene.choices.choice2.result, savedChoices)
    if (choice2Result === sceneId && savedChoices[scene.id] === 2) return true
  }
  return false
}
